import type { Gender, UserDetail } from 'models';
import {
  BirthDatePicker,
  type BirthDateType,
  Col,
  Form,
  FormActions,
  Modal,
  Paragraph,
  PhoneInput,
  ProgressIndicator,
  Row,
  SelectInput,
  Space,
  Title,
  getBirthDateTypeValue,
  useBirthDateValidation,
} from 'ui';
import { useUser } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { AddressFieldset, NameFieldset } from 'features/users/profile-form';

export type PersonalInfoFormValues = {
  apt: string;
  birthdate: BirthDateType;
  city: string;
  country: string;
  email: string;
  firstName: string;
  gender: string;
  lastName: string;
  phone: string;
  state: string;
  street: string;
  zip: string;
};

const getInitialValues = ({ viewer }: { viewer: UserDetail }): Partial<PersonalInfoFormValues> => ({
  apt: viewer.apt,
  birthdate: getBirthDateTypeValue(viewer.birthdate),
  city: viewer.city,
  country: viewer.country,
  email: viewer.email,
  firstName: viewer.firstName,
  gender: viewer.gender?.id,
  lastName: viewer.lastName,
  phone: viewer.phone,
  state: viewer.state,
  street: viewer.street,
  zip: viewer.zip,
});

type P = {
  genders: Gender[];
  initialFormValues?: Partial<PersonalInfoFormValues>;
  isLoading: boolean;
  onBack: (formValues: Partial<PersonalInfoFormValues>) => void;
  onSubmit: (values: PersonalInfoFormValues) => void;
};

const PersonalInfoForm = ({ genders, initialFormValues, isLoading, onBack, onSubmit }: P) => {
  const [form] = Form.useForm<PersonalInfoFormValues>();
  const { railsUserData } = useUser();

  const birthDateValidatorRule = useBirthDateValidation();

  return (
    <Form<PersonalInfoFormValues>
      form={form}
      initialValues={initialFormValues ?? getInitialValues({ viewer: railsUserData })}
      onFinish={onSubmit}
    >
      <Space orientation="vertical" size="middle">
        <ProgressIndicator
          progressProperties={{ size: 'extra-small' }}
          step={2}
          textProperties={{ type: 'title' }}
          total={3}
        />
        <Paragraph>
          Please confirm you are using your legal name and the information below is correct by clicking the continue
          button below. After confirmation, any changes will be reflected in your profile.
        </Paragraph>

        <Row gutter="xs">
          <Col span={24}>
            <Title level={3} marginBottom>
              About me
            </Title>
          </Col>

          <Col span={24}>
            <NameFieldset<PersonalInfoFormValues> withMiddleName={false} />
          </Col>

          <Col span={24}>
            <PhoneInput
              item={{
                label: 'Phone number',
                name: 'phone',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>

          <Col span={24}>
            <Form.Item<PersonalInfoFormValues>
              label="Date of birth"
              name="birthdate"
              rules={[birthDateValidatorRule({ isRequired: true }), { required: true }]}
            >
              <BirthDatePicker />
            </Form.Item>
          </Col>

          <Col span={24}>
            <SelectInput
              input={{
                getOptionLabel: (option) => option.name,
                getOptionValue: (option) => option.id,
                options: genders,
              }}
              item={{
                label: 'Gender',
                name: 'gender',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>

          <Col span={24}>
            <Title level={3} marginBottom>
              Home Address
            </Title>
          </Col>

          <Col span={24}>
            <AddressFieldset<PersonalInfoFormValues> isRequired />
          </Col>
        </Row>
      </Space>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            children: 'Back',
            onClick() {
              onBack(form.getFieldsValue());
            },
          }}
          submit={{
            children: 'Continue',
            loading: isLoading,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default PersonalInfoForm;
